import { useCallback, useRef } from 'react';

/**
 * Wraps an async action so only one call runs at a time; calls made while it is
 * in flight resolve to `undefined` without running it.
 *
 * The guard is a ref, not state: `isPending` only flips after a re-render, so a
 * double click in the same tick would otherwise send two requests.
 */
export function useSingleFlight<Args extends unknown[], Result>(
    action: (...args: Args) => Promise<Result>,
): (...args: Args) => Promise<Result | undefined> {
    const inFlight = useRef(false);

    return useCallback(
        async (...args: Args) => {
            if (inFlight.current) {
                return undefined;
            }

            inFlight.current = true;
            try {
                return await action(...args);
            } finally {
                inFlight.current = false;
            }
        },
        [action],
    );
}
